import { difference, omit, uniq } from 'ramda';
import { isoDateStringNoTimeDiff, numberOfDays } from 'c-lib';
import type { Campaign, CampaignSyncLineItemsRequest } from 'c-sdk';
import type { NewCampaignFormSchema, UpdateCampaignLineItemsFormSchema } from 'c-main/Types';
import { CampaignFormNames } from 'c-main/Types';
import { campaignLineItemSettings } from './campaignLineItems';

/**
 * Builders for the create and edit-line-items forms: the defaults the edit form starts with and
 * the request body each one sends. Extracted from `NewCampaignForm` and `EditCampaignLineItems`
 * so the payloads can be tested without walking the steppers.
 */

/** Days the campaign runs, inclusive of both ends; what a line item without its own days gets. */
export const campaignFormMaxDays = (
    data: Pick<NewCampaignFormSchema, 'start_datetime' | 'end_datetime'>,
) => numberOfDays(new Date(data.start_datetime ?? ''), new Date(data.end_datetime ?? ''));

/** The body `POST campaign/create` gets from the new-campaign form. */
export const buildCreateCampaignRequest = (data: NewCampaignFormSchema) => ({
    ...omit(['start_dates', 'end_dates', 'days'], data),
    line_items: campaignLineItemSettings(data, campaignFormMaxDays(data)),
    [CampaignFormNames.agency_name]: data[CampaignFormNames.client_name],
});

/** The body `campaign/:id/sync-line-items` gets from the edit-line-items form. */
export const buildSyncLineItemsRequest = (
    data: UpdateCampaignLineItemsFormSchema,
): CampaignSyncLineItemsRequest => ({
    attach: campaignLineItemSettings(data, campaignFormMaxDays(data)),
    detach: difference(data.originalLineItems ?? [], data.line_items ?? []),
    start_datetime: data.start_datetime,
    end_datetime: data.end_datetime,
});

const budgetLineItems = (campaign: Campaign | null | undefined) =>
    campaign?.budgetSummary?.lineItems ?? null;

const budgetDate = (date: unknown) =>
    typeof date === 'string' ? isoDateStringNoTimeDiff(date) : '';

export const campaignLineItemIds = (campaign: Campaign | null | undefined) =>
    uniq(campaign?.lineItems?.map(item => String(item.id)) ?? []);

/** Days per line item from the current budget summary; undefined when there is no summary. */
export const campaignLineItemDays = (campaign: Campaign | null | undefined) =>
    budgetLineItems(campaign)?.reduce<Record<string, number>>(
        (accDays, lineItem) => ({ ...accDays, [lineItem.line_item_id]: lineItem.days }),
        {},
    );

export const campaignLineItemStartDates = (campaign: Campaign | null | undefined) =>
    budgetLineItems(campaign)?.reduce<Record<string, string>>(
        (acc, lineItem) => ({ ...acc, [lineItem.line_item_id]: budgetDate(lineItem.start_date) }),
        {},
    );

export const campaignLineItemEndDates = (campaign: Campaign | null | undefined) =>
    budgetLineItems(campaign)?.reduce<Record<string, string>>(
        (acc, lineItem) => ({ ...acc, [lineItem.line_item_id]: budgetDate(lineItem.end_date) }),
        {},
    );

/** What the edit-line-items form starts with for a campaign. */
export const editCampaignLineItemsDefaults = (campaign: Campaign) =>
    ({
        name: campaign.name,
        reference: campaign.reference,
        buyer_id: campaign.buyer_id,
        agency_id: campaign.agency_id,
        client_id: campaign.client_id,
        start_datetime: isoDateStringNoTimeDiff(campaign.date_start),
        end_datetime: isoDateStringNoTimeDiff(campaign.date_end),

        line_items: campaignLineItemIds(campaign),
        start_dates: campaignLineItemStartDates(campaign),
        end_dates: campaignLineItemEndDates(campaign),
        days: campaignLineItemDays(campaign),

        originalLineItems: campaignLineItemIds(campaign),
        originalDays: campaignLineItemDays(campaign),
    } as UpdateCampaignLineItemsFormSchema);
